import * as THREE from 'three';
import store from '../redux/store';

const DEFAULT_AUDIO_SETTINGS = {
  volume: 0.5,
  loop: false,
  mute: false,
  pitch: 1,
  spatial_blend: 0,
};

const PREVIEW_AUDIO_PREFIX = 'preview_audio_';
const bufferCache = new Map();
const bufferLoadPromises = new Map();
const audioLoader = new THREE.AudioLoader();
let playbackGeneration = 0;

export function invalidateAudioPlayback() {
  playbackGeneration += 1;
}

function ensureChildrenExtensible(obj) {
  if (obj?.children && !Object.isExtensible(obj.children)) {
    obj.children = [...obj.children];
  }
}

export function isAudioMesh(mesh) {
  if (!mesh) return false;
  return (
    mesh.deleteType === 'audio' ||
    !!mesh.audioElement ||
    !!getHtmlAudioFromMesh(mesh) ||
    !!getDesignAudioFromMesh(mesh) ||
    !!getUploadPositionalAudio(mesh)
  );
}

export function getAudioSettings(mesh) {
  if (!mesh) return { ...DEFAULT_AUDIO_SETTINGS };
  return {
    ...DEFAULT_AUDIO_SETTINGS,
    ...(mesh.userData?.audio_settings || {}),
    ...(mesh.audio_settings || {}),
  };
}

/** AudioComponent design-mode object (buffer-based, correct volume). */
export function getDesignAudioFromMesh(mesh) {
  if (!mesh) return null;
  const configured = mesh.getObjectByName?.(`audio_${mesh.uuid}`);
  if (
    configured &&
    (configured.type === 'Audio' || configured.type === 'PositionalAudio') &&
    configured.buffer
  ) {
    return configured;
  }
  return null;
}

function getUploadPositionalAudio(mesh) {
  if (!mesh) return null;
  let found = null;
  mesh.traverse?.((o) => {
    if (found) return;
    if (
      (o.type === 'PositionalAudio' || o.type === 'Audio') &&
      o.name !== `${PREVIEW_AUDIO_PREFIX}${mesh.uuid}` &&
      !String(o.name || '').startsWith('audio_')
    ) {
      found = o;
    }
  });
  return found;
}

export function getHtmlAudioFromMesh(mesh) {
  if (!mesh) return null;
  if (mesh.audioElement && typeof mesh.audioElement.play === 'function') {
    return mesh.audioElement;
  }
  let el = null;
  mesh.traverse?.((o) => {
    if (el) return;
    if (
      (o.type === 'PositionalAudio' || o.type === 'Audio') &&
      o.source?.mediaElement
    ) {
      el = o.source.mediaElement;
    }
  });
  return el;
}

export function getAudioSourceUrl(mesh) {
  if (!mesh) return null;
  const fromMesh = mesh.asset_file_path || mesh.userData?.asset_file_path;
  if (fromMesh) return fromMesh;
  const settings = getAudioSettings(mesh);
  const fromSettings = settings.audio_source || settings.audio_clip;
  if (fromSettings) return fromSettings;

  const htmlAudio = getHtmlAudioFromMesh(mesh);
  const fromElement = htmlAudio?.currentSrc || htmlAudio?.src;
  if (fromElement) return fromElement;

  const assetsDataAudio =
    store.getState()?.assets_object?.assets_data?.audio || [];
  const match = assetsDataAudio.find(
    (a) =>
      (a._id &&
        (a._id === mesh.name || a._id === mesh._id || a._id === mesh.url)) ||
      (a.file_name && a.file_name === mesh.file_name)
  );
  return match?.asset_file_path || null;
}

export function resumeAudioContext() {
  const ctx = THREE.AudioContext?.getContext?.();
  if (ctx?.state === 'suspended') {
    ctx.resume();
  }
}

function getActiveCamera() {
  return (
    (typeof window !== 'undefined' && window.camera) ||
    null
  );
}

function getOrAttachAudioListener(camera) {
  if (!camera) return null;
  let listener = null;
  camera.traverse?.((child) => {
    if (!listener && child.type === 'AudioListener') {
      listener = child;
    }
  });
  if (!listener) {
    listener = new THREE.AudioListener();
    camera.add(listener);
  }
  return listener;
}

function loadAudioBuffer(url) {
  if (bufferCache.has(url)) {
    return Promise.resolve(bufferCache.get(url));
  }
  if (bufferLoadPromises.has(url)) {
    return bufferLoadPromises.get(url);
  }
  const promise = new Promise((resolve, reject) => {
    audioLoader.load(
      url,
      (buffer) => {
        bufferCache.set(url, buffer);
        bufferLoadPromises.delete(url);
        resolve(buffer);
      },
      undefined,
      (err) => {
        bufferLoadPromises.delete(url);
        reject(err);
      }
    );
  });
  bufferLoadPromises.set(url, promise);
  return promise;
}

/** Stop upload-time PositionalAudio / HTML so only one path plays (matches design loudness). */
function silenceLegacyAudioSources(mesh, activeAudio, { keepHtml = false } = {}) {
  if (!keepHtml) {
    const html = getHtmlAudioFromMesh(mesh);
    if (html) {
      try {
        html.pause();
      } catch (_) {
        /* ignore */
      }
      html.muted = true;
      html.volume = 0;
    }
  }

  mesh.traverse?.((o) => {
    if (o === activeAudio) return;
    if (o.type !== 'Audio' && o.type !== 'PositionalAudio') return;
    try {
      if (o.isPlaying) {
        if (typeof o.stop === 'function') o.stop();
        else if (typeof o.pause === 'function') o.pause();
      }
    } catch (_) {
      /* ignore */
    }
  });
}

export function applyAudioSettingsToThreeAudio(threeAudio, settings) {
  if (!threeAudio) return;

  const volume = settings.mute
    ? 0
    : (settings.volume ?? DEFAULT_AUDIO_SETTINGS.volume);
  if (typeof threeAudio.setVolume === 'function') {
    threeAudio.setVolume(volume);
  }

  if (typeof threeAudio.setLoop === 'function') {
    threeAudio.setLoop(!!settings.loop);
  }

  const pitch = Number(settings.pitch ?? DEFAULT_AUDIO_SETTINGS.pitch);
  if (typeof threeAudio.setPlaybackRate === 'function') {
    // pitch === 0 means no shift (normal speed = rate 1); negative pitch is handled
    // by buffer reversal elsewhere, so clamp rate to a valid positive value.
    const rate = pitch === 0 ? 1 : Math.abs(pitch);
    threeAudio.setPlaybackRate(rate);
  }

  const spatialBlend = Number(
    settings.spatial_blend ?? DEFAULT_AUDIO_SETTINGS.spatial_blend
  );
  if (threeAudio instanceof THREE.PositionalAudio) {
    const blend = Math.max(0, Math.min(1, spatialBlend));
    threeAudio.setRefDistance(1 + 9 * blend);
    threeAudio.setRolloffFactor(blend > 0 ? blend : 0);
    // At blend=0 use a very large max distance + linear model so it behaves like 2D.
    // At blend>0 use inverse rolloff (Web Audio default) for natural distance falloff.
    if (blend <= 0) {
      threeAudio.setDistanceModel('linear');
      threeAudio.setMaxDistance(10000);
      threeAudio.setRolloffFactor(0);
    } else {
      threeAudio.setDistanceModel('inverse');
      threeAudio.setMaxDistance(10000);
    }
  }
}

function getOrCreatePreviewAudio(mesh, listener, settings) {
  const previewName = `${PREVIEW_AUDIO_PREFIX}${mesh.uuid}`;
  let audio = mesh.getObjectByName?.(previewName);

  const spatialBlend = Number(
    settings.spatial_blend ?? DEFAULT_AUDIO_SETTINGS.spatial_blend
  );
  const usePositional = spatialBlend > 0;
  const AudioClass = usePositional ? THREE.PositionalAudio : THREE.Audio;

  if (audio && !(audio instanceof AudioClass)) {
    try {
      audio.stop();
    } catch (_) {
      /* ignore */
    }
    mesh.remove(audio);
    audio = null;
  }

  if (!audio) {
    audio = new AudioClass(listener);
    audio.name = previewName;
    ensureChildrenExtensible(mesh);
    mesh.add(audio);
  }

  return audio;
}

function playViaHtmlFallback(mesh, settings) {
  const generation = playbackGeneration;
  const html = getHtmlAudioFromMesh(mesh);
  if (!html) return false;

  const url = getAudioSourceUrl(mesh);
  if (url && url !== html.src) {
    html.src = url;
    html.load();
  }

  html.muted = !!settings.mute;
  html.loop = !!settings.loop;
  html.volume = settings.mute
    ? 0
    : (settings.volume ?? DEFAULT_AUDIO_SETTINGS.volume);
  const pitch = Number(settings.pitch ?? DEFAULT_AUDIO_SETTINGS.pitch);
  // pitch === 0 means no shift (normal speed). Reset playbackRate to 1 so
  // any previously applied rate doesn't persist.
  html.playbackRate = pitch === 0 ? 1 : Math.abs(pitch);

  silenceLegacyAudioSources(mesh, null, { keepHtml: true });

  const start = () => {
    if (generation !== playbackGeneration) return;
    resumeAudioContext();
    const p = html.play();
    if (p?.catch) p.catch(() => {});
  };

  if (html.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    start();
  } else {
    html.addEventListener('canplay', start, { once: true });
  }
  return true;
}

function stopThreeAudioNode(audio) {
  if (!audio) return;
  try {
    if (typeof audio.stop === 'function') audio.stop();
    else if (typeof audio.pause === 'function') audio.pause();
  } catch (_) {
    /* ignore */
  }
}

function restoreHtmlElement(html, mesh) {
  if (!html) return;
  try {
    html.pause();
  } catch (_) {
    /* ignore */
  }
  html.muted = false;
  if (mesh) {
    const settings = getAudioSettings(mesh);
    html.volume = settings.mute
      ? 0
      : (settings.volume ?? DEFAULT_AUDIO_SETTINGS.volume);
  }
}

export function removePreviewAudioFromScene(scene) {
  if (!scene) return;
  const removals = [];
  scene.traverse((obj) => {
    if (obj.name?.startsWith(PREVIEW_AUDIO_PREFIX)) {
      stopThreeAudioNode(obj);
      if (obj.parent) removals.push({ parent: obj.parent, child: obj });
    }
  });
  removals.forEach(({ parent, child }) => {
    ensureChildrenExtensible(parent);
    parent.remove(child);
  });
}

/** Stop every audio source in the scene (used when switching design ↔ preview). */
export function stopAllSceneAudio(scene) {
  invalidateAudioPlayback();
  if (!scene) return;

  removePreviewAudioFromScene(scene);

  const seenHtml = new Set();
  scene.traverse((obj) => {
    if (obj.type === 'Audio' || obj.type === 'PositionalAudio') {
      stopThreeAudioNode(obj);
    }

    if (obj.deleteType === 'audio') {
      if (obj.audioElement && !seenHtml.has(obj.audioElement)) {
        seenHtml.add(obj.audioElement);
        restoreHtmlElement(obj.audioElement, obj);
      }
      const html = getHtmlAudioFromMesh(obj);
      if (html && !seenHtml.has(html)) {
        seenHtml.add(html);
        restoreHtmlElement(html, obj);
      }
    }
  });

  const sceneAudios = store.getState()?.assets_object?.sceneAudios || [];
  sceneAudios.forEach((mesh) => {
    if (mesh?.isObject3D) stopMeshAudio(mesh);
  });
}

async function playViaWebAudioBuffer(mesh) {
  const url = getAudioSourceUrl(mesh);
  if (!url) return false;

  const generation = playbackGeneration;
  const settings = getAudioSettings(mesh);
  const camera = getActiveCamera();
  const listener = getOrAttachAudioListener(camera);
  if (!listener) {
    return playViaHtmlFallback(mesh, settings);
  }

  resumeAudioContext();

  try {
    let threeAudio = getDesignAudioFromMesh(mesh);

    if (!threeAudio) {
      threeAudio = getOrCreatePreviewAudio(mesh, listener, settings);
      const buffer = await loadAudioBuffer(url);
      if (generation !== playbackGeneration) return false;
      threeAudio.setBuffer(buffer);
    }

    if (generation !== playbackGeneration) return false;

    silenceLegacyAudioSources(mesh, threeAudio);
    applyAudioSettingsToThreeAudio(threeAudio, settings);

    if (!threeAudio.isPlaying) {
      threeAudio.play();
    }

    return true;
  } catch (err) {
    if (generation !== playbackGeneration) return false;
    console.warn('[audioPlayback] buffer load failed, using HTML fallback:', err);
    return playViaHtmlFallback(mesh, settings);
  }
}

/** Matches AudioComponent: play_on_awake defaults to true when unset. */
export function shouldPlayOnAwake(mesh) {
  const settings = getAudioSettings(mesh);
  return settings.play_on_awake !== false;
}

/** In preview, auto-play audio assets that have Play On Awake enabled. */
export function playPreviewAudioOnAwake(scene) {
  if (!scene) return;

  const seen = new Set();
  const tryPlay = (mesh) => {
    if (!mesh?.uuid || seen.has(mesh.uuid)) return;
    if (!isAudioMesh(mesh)) return;
    seen.add(mesh.uuid);
    if (shouldPlayOnAwake(mesh)) {
      playMeshAudio(mesh);
    }
  };

  const sceneAudios = store.getState()?.assets_object?.sceneAudios || [];
  sceneAudios.forEach(tryPlay);
  scene.traverse((obj) => {
    if (obj.deleteType === 'audio') tryPlay(obj);
  });
}

/**
 * Preview playback: same Web Audio / buffer path as design mode (AudioComponent).
 * Avoids HTML + PositionalAudio double routing that makes preview sound much quieter.
 */
export function playMeshAudio(mesh) {
  if (!mesh) return false;

  playViaWebAudioBuffer(mesh).catch((err) => {
    console.warn('[audioPlayback] play failed:', err);
    playViaHtmlFallback(mesh, getAudioSettings(mesh));
  });

  return true;
}

export function pauseMeshAudio(mesh) {
  if (!mesh) return false;

  let handled = false;
  const targets = [
    getDesignAudioFromMesh(mesh),
    mesh.getObjectByName?.(`${PREVIEW_AUDIO_PREFIX}${mesh.uuid}`),
    getUploadPositionalAudio(mesh),
  ].filter(Boolean);

  targets.forEach((audio) => {
    if (audio.isPlaying) {
      if (typeof audio.pause === 'function') audio.pause();
      else if (typeof audio.stop === 'function') audio.stop();
      handled = true;
    }
  });

  const html = getHtmlAudioFromMesh(mesh);
  if (html && !html.paused) {
    html.pause();
    handled = true;
  }
  return handled;
}

export function stopMeshAudio(mesh) {
  if (!mesh) return false;

  let handled = false;
  const targets = [
    getDesignAudioFromMesh(mesh),
    mesh.getObjectByName?.(`${PREVIEW_AUDIO_PREFIX}${mesh.uuid}`),
    getUploadPositionalAudio(mesh),
  ].filter(Boolean);

  targets.forEach((audio) => {
    try {
      if (typeof audio.stop === 'function') audio.stop();
      else if (typeof audio.pause === 'function') audio.pause();
      handled = true;
    } catch (_) {
      /* ignore */
    }
  });

  const html = getHtmlAudioFromMesh(mesh);
  if (html) {
    html.pause();
    html.currentTime = 0;
    handled = true;
  }
  return handled;
}
